/**
 * Block Color Utilities
 *
 * Generates muted, varied colors for each block using golden ratio distribution.
 * Colors are desaturated and slightly dimmed for a softer look.
 */

import { ThemeManager } from '../rendering/ThemeManager';
import { BASE_HUE, ACCENT_PRIMARY_HEX } from '../config/themes';

// Color palette - generated with varied hues, includes accent color
// Uses the BASE_HUE as the primary color in the palette
const MUTED_COLORS: number[] = [
  ACCENT_PRIMARY_HEX,  // Primary accent (from theme)
  0x6bb85e, // sage green
  0xc4a06a, // tan/gold
  0x9a6bc4, // lavender
  0xc46b8a, // mauve/pink
  0x6b8ac4, // steel blue
  0xb8b85e, // olive/yellow
  0x4db8b8, // teal
  0xc46b6b, // rose/coral
  0x6b6bc4, // periwinkle
  0x8ab86b, // moss green
  0xc49a9a, // dusty pink
];

/**
 * Get color for a block number
 * Returns a muted color from the palette based on block number
 *
 * @param blockNumber - The block number
 * @returns RGB color as number (0xRRGGBB)
 */
export function getBlockColor(blockNumber: number): number {
  const index = blockNumber % MUTED_COLORS.length;
  return MUTED_COLORS[index];
}

/**
 * Get a contrasting version of a block color for transaction bullets
 * In dark mode: brightens for visibility against dark background
 * In light mode: darkens for visibility against light background
 *
 * @param blockNumber - The block number
 * @returns RGB color as number (0xRRGGBB) - adjusted for theme
 */
export function getBulletColor(blockNumber: number): number {
  const baseColor = getBlockColor(blockNumber);

  // Extract RGB components
  const r = (baseColor >> 16) & 0xff;
  const g = (baseColor >> 8) & 0xff;
  const b = baseColor & 0xff;

  const isLightMode = ThemeManager.mode === 'light';

  if (isLightMode) {
    // Darken for light mode - reduce each channel significantly
    const darken = (c: number): number => {
      return Math.max(0, Math.round(c * 0.5)); // 50% darker
    };

    const newR = darken(r);
    const newG = darken(g);
    const newB = darken(b);

    return (newR << 16) | (newG << 8) | newB;
  } else {
    // Brighten for dark mode (original behavior)
    const brighten = (c: number): number => {
      let brightened = Math.min(255, Math.round(c * 1.4));
      brightened = Math.min(255, brightened + 30);
      return brightened;
    };

    const newR = brighten(r);
    const newG = brighten(g);
    const newB = brighten(b);

    return (newR << 16) | (newG << 8) | newB;
  }
}
